// Picks a sensible default image for a product when no image has been uploaded.
// Order of preference: unit → category keywords → product-name keywords → generic.
// Returns a path under /uploads/products/defaults/<key>.svg

const UNIT_MAP = {
  tablet: 'tablet',
  capsule: 'capsule',
  blister: 'blister',
  sachet: 'sachet',
  bottle: 'bottle',
  ml: 'syrup',     // liquid measure → bottle/syrup
  l: 'bottle',
  drop: 'drops',
  drops: 'drops',
  box: 'box',
  carton: 'carton',
  pack: 'pack',
  bag: 'bag',
  kg: 'bag',
  g: 'sachet',
  dozen: 'pcs',
  pcs: 'pcs',
  m: 'tools',
  metre: 'tools',
  meter: 'tools',
};

// Keyword patterns matched against category name OR product name (case-insensitive).
// First match wins, so order matters — most specific first.
const KEYWORD_MAP = [
  // Pharmacy specifics
  { test: /capsule|cap\b/i, key: 'capsule' },
  { test: /blister|strip/i, key: 'blister' },
  { test: /tablet|tab\b|pill/i, key: 'tablet' },
  { test: /syrup|suspension/i, key: 'syrup' },
  { test: /drop\b|drops/i, key: 'drops' },
  { test: /inject|vaccin|amp(oule)?/i, key: 'injection' },
  { test: /ointment|cream|gel|balm/i, key: 'ointment' },
  { test: /plaster|bandage|gauze|tape/i, key: 'bandage' },
  { test: /thermometer|infrared/i, key: 'thermometer' },
  { test: /pain relief|analgesic|antibiotic|wellness|first aid|pharmacy/i, key: 'tablet' },

  // Beverages / food
  { test: /water|soda|cola|beverage|juice|soft drink|drink/i, key: 'drink' },
  { test: /bottle/i, key: 'bottle' },
  { test: /rice|sugar|flour|maize|grain|pantry|grocery|fresh/i, key: 'grocery' },
  { test: /yoghurt|dairy|milk|cheese|butter/i, key: 'bottle' },
  { test: /snack|biscuit|chocolate|cookie|food|meal|jollof|plate/i, key: 'food' },

  // Retail forms
  { test: /carton/i, key: 'carton' },
  { test: /sachet|pouch|packet/i, key: 'sachet' },
  { test: /\bbag\b/i, key: 'bag' },
  { test: /\bbox\b/i, key: 'box' },
  { test: /\bpack(et)?\b/i, key: 'pack' },

  // Industries
  { test: /clothing|fashion|shirt|jean|dress|boutique|footwear|sandal|shoe/i, key: 'clothing' },
  { test: /electronic|phone|laptop|charger|battery|earbud|speaker|appliance/i, key: 'electronics' },
  { test: /book|notebook|pen|pencil|paper|stationery|atlas|dictionary/i, key: 'book' },
  { test: /hammer|screw|hardware|tool|cement|pipe|wrench|cable|bulb/i, key: 'tools' },
  { test: /auto|engine|spark|brake|filter|wiper|oil|lubricant/i, key: 'auto' },
  { test: /cosmetic|beauty|lipstick|shea|cream|shampoo|perfume|fragrance|skincare|hair/i, key: 'cosmetics' },
];

export const defaultImageFor = (product = {}) => {
  const unit = String(product.unit || '').toLowerCase().trim();
  if (unit && UNIT_MAP[unit]) {
    return `/uploads/products/defaults/${UNIT_MAP[unit]}.svg`;
  }

  const haystack = `${product.category_name || ''} ${product.name || ''} ${product.description || ''}`.trim();
  if (haystack) {
    for (const { test, key } of KEYWORD_MAP) {
      if (test.test(haystack)) return `/uploads/products/defaults/${key}.svg`;
    }
  }
  return '/uploads/products/defaults/generic.svg';
};

// Helper: which default keys are available for explicit pick in the UI.
export const DEFAULT_IMAGE_KEYS = [
  'tablet', 'capsule', 'blister', 'syrup', 'drops', 'injection',
  'ointment', 'bandage', 'thermometer',
  'box', 'carton', 'sachet', 'pack', 'bag', 'bottle', 'drink', 'food', 'grocery',
  'clothing', 'electronics', 'book', 'tools', 'auto', 'cosmetics',
  'pcs', 'generic',
];
